class ApiService {
  constructor() {
    this.baseURL = '/api';
  }

  async request(endpoint, options = {}) {
    const token = localStorage.getItem('csense_auth_token');
    const url = `${this.baseURL}${endpoint}`;
    
    console.log('🔍 Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'null');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('csense_auth_token');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Don't redirect immediately, let the app handle it
          throw new Error('Authentication required');
        }
        
        if (response.status === 403) {
          throw new Error('Access denied - insufficient permissions');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // Authentication
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }

  async refreshToken() {
    return this.request('/auth/refresh', { method: 'POST' });
  }

  // User management
  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async updateProfile(profileData) {
    return this.request('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }

  // Multi-tenant specific
  async getAllowedCameras() {
    return this.request('/me/allowed-cameras');
  }

  async getUserPermissions() {
    return this.request('/me/permissions');
  }

  // Cameras (now organization-filtered)
  async getCameras() {
    return this.request('/cameras');
  }

  async getCameraById(cameraId) {
    return this.request(`/cameras/${cameraId}`);
  }

  async addCamera(cameraData) {
    return this.request('/cameras/add-simple', {
      method: 'POST',
      body: JSON.stringify(cameraData)
    });
  }

  async updateCamera(cameraId, cameraData) {
    return this.request(`/cameras/${cameraId}`, {
      method: 'PUT',
      body: JSON.stringify(cameraData)
    });
  }

  async deleteCamera(cameraId) {
    return this.request(`/cameras/${cameraId}`, {
      method: 'DELETE'
    });
  }

  async assignCameraUsecase(cameraId, usecase) {
    return this.request(`/cameras/${cameraId}/assign-usecase`, {
      method: 'POST',
      body: JSON.stringify({ usecase })
    });
  }

  async startDetection(cameraId) {
    return this.request(`/cameras/${cameraId}/start-detection`, {
      method: 'POST'
    });
  }

  async stopDetection(cameraId) {
    return this.request(`/cameras/${cameraId}/stop-detection`, {
      method: 'POST'
    });
  }

  // Alerts (now organization-filtered)
  async getAlerts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/alerts${queryString ? `?${queryString}` : ''}`);
  }

  async markAlertRead(alertId) {
    return this.request(`/alerts/${alertId}/read`, {
      method: 'POST'
    });
  }

  // Subuser management (owner/admin only)
  async createSubuser(subuserData) {
    return this.request('/subusers', {
      method: 'POST',
      body: JSON.stringify(subuserData)
    });
  }

  async getSubusers() {
    return this.request('/subusers');
  }

  async updateSubuserPermissions(subuserId, permissions) {
    return this.request(`/subusers/${subuserId}`, {
      method: 'PUT',
      body: JSON.stringify(permissions)
    });
  }

  async deleteSubuser(subuserId) {
    return this.request(`/subusers/${subuserId}`, {
      method: 'DELETE'
    });
  }

  // Location management
  async getLocations() {
    return this.request('/management/locations');
  }

  async createLocation(locationData) {
    return this.request('/management/locations', {
      method: 'POST',
      body: JSON.stringify(locationData)
    });
  }

  async searchLocations(query) {
    return this.request(`/management/locations/search?query=${encodeURIComponent(query)}`);
  }

  // Statistics
  async getStats() {
    return this.request('/stats');
  }

  async getDashboardAnalytics() {
    return this.request('/dashboard/analytics');
  }

  // Notifications
  async getNotifications(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/notifications${queryString ? `?${queryString}` : ''}`);
  }

  async markNotificationRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'POST'
    });
  }
}

export default new ApiService();